import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { CURRENT_SEASON } from '@/lib/season'
import { FINISHED_STATUSES } from '@/lib/fixture-status'

export interface PickableMatchday {
  matchday: number
  firstKickoff: Date
  isLocked: boolean
  isCompleted: boolean
  hasEarlyFixture: boolean
}

// 72h threshold avoids flagging normal Sat 12:30 → Sun 2pm EPL weekends
const EARLY_FIXTURE_GAP_HOURS = 72

/**
 * Detect whether a set of kickoff times has an "early fixture" —
 * one fixture kicking off significantly earlier than the bulk.
 */
export function hasEarlyFixtureGap(kickoffs: Date[]): boolean {
  if (kickoffs.length < 3) return false
  const sorted = [...kickoffs].sort((a, b) => a.getTime() - b.getTime())
  const medianKickoff = sorted[Math.floor(sorted.length / 2)]
  const gapHours = (medianKickoff.getTime() - sorted[0].getTime()) / (1000 * 60 * 60)
  return gapHours > EARLY_FIXTURE_GAP_HOURS
}

/**
 * Returns upcoming matchdays with metadata for navigation.
 * A matchday is included if it has at least one non-finished fixture.
 * "isLocked" uses the absolute earliest kickoff (including finished fixtures)
 * so that a matchday stays locked even after its early fixture finishes.
 * "hasEarlyFixture" means one fixture kicks off significantly earlier than the rest.
 */
export function usePickableMatchdays(season: string = CURRENT_SEASON, competitionId?: number, leagueId?: string) {
  return useQuery({
    queryKey: ['pickable-matchdays', season, competitionId, leagueId],
    enabled: competitionId != null,
    queryFn: async () => {
      // Fetch fixture exclusions for this league+season
      let excludedFixtureIds = new Set<string>()
      if (leagueId) {
        const { data: exclusions } = await supabase
          .from('fixture_exclusions')
          .select('fixture_id')
          .eq('league_id', leagueId)
          .eq('season', season)
        if (exclusions) {
          excludedFixtureIds = new Set(exclusions.map((r) => r.fixture_id))
        }
      }

      // 1. Find matchdays that have at least one non-finished fixture
      let pendingQuery = supabase
        .from('fixtures')
        .select('matchday')
        .eq('season', season)
        .not('status', 'in', `(${FINISHED_STATUSES.join(',')})`)
        .order('matchday', { ascending: true });
      if (competitionId != null) pendingQuery = pendingQuery.eq('competition_id', competitionId);

      const { data: pendingData, error: pendingError } = await pendingQuery;

      if (pendingError) {
        console.error('Error fetching pending matchdays:', pendingError)
        throw pendingError
      }

      const pendingMatchdays = pendingData
        ? [...new Set(pendingData.map((r) => r.matchday as number))].sort((a, b) => a - b)
        : []
      const pendingSet = new Set(pendingMatchdays)

      // 2. Find the most recently completed matchday (all fixtures finished,
      //    not in the pending list). We fetch the one with the latest kickoff.
      let completedQuery = supabase
        .from('fixtures')
        .select('matchday')
        .eq('season', season)
        .in('status', FINISHED_STATUSES)
        .order('kickoff_time', { ascending: false })
        .limit(50);
      if (competitionId != null) completedQuery = completedQuery.eq('competition_id', competitionId);

      const { data: completedData } = await completedQuery;

      // Find distinct matchdays from completed fixtures that aren't in the pending list
      const completedMatchdays: number[] = []
      if (completedData) {
        const seen = new Set<number>()
        for (const row of completedData) {
          const md = row.matchday as number
          if (!pendingSet.has(md) && !seen.has(md)) {
            seen.add(md)
            completedMatchdays.push(md)
            if (completedMatchdays.length >= 2) break // keep last 2 completed
          }
        }
      }

      const allMatchdays = [...completedMatchdays, ...pendingMatchdays]
      if (allMatchdays.length === 0) return []

      // 3. Fetch ALL fixtures for those matchdays (including finished)
      //    so isLocked and hasEarlyFixture use the true earliest kickoff
      let allQuery = supabase
        .from('fixtures')
        .select('id, matchday, kickoff_time')
        .eq('season', season)
        .in('matchday', allMatchdays)
        .order('matchday', { ascending: true })
        .order('kickoff_time', { ascending: true });
      if (competitionId != null) allQuery = allQuery.eq('competition_id', competitionId);

      const { data: allData, error: allError } = await allQuery;

      if (allError) {
        console.error('Error fetching matchday fixtures:', allError)
        throw allError
      }

      if (!allData || allData.length === 0) return []

      const now = new Date()

      // Group all fixtures by matchday, tracking excluded status
      const byMatchday = new Map<number, { all: Date[]; nonExcluded: Date[] }>()
      for (const row of allData) {
        const md = row.matchday as number
        const kickoff = new Date(row.kickoff_time as string)
        if (!byMatchday.has(md)) byMatchday.set(md, { all: [], nonExcluded: [] })
        const entry = byMatchday.get(md)!
        entry.all.push(kickoff)
        if (!excludedFixtureIds.has(row.id as string)) {
          entry.nonExcluded.push(kickoff)
        }
      }

      const result: PickableMatchday[] = []
      for (const matchday of allMatchdays) {
        const entry = byMatchday.get(matchday)
        if (!entry) continue

        // Use non-excluded kickoffs for deadline if there are exclusions,
        // otherwise fall back to all kickoffs
        const deadlineKickoffs = entry.nonExcluded.length > 0 ? entry.nonExcluded : entry.all
        deadlineKickoffs.sort((a, b) => a.getTime() - b.getTime())
        const firstKickoff = deadlineKickoffs[0]
        // Deadline = 1 minute before kickoff, matching deadlineDate in MatchdaySection
        const deadline = new Date(firstKickoff.getTime() - 60 * 1000)
        const isLocked = deadline <= now
        const isCompleted = !pendingSet.has(matchday)
        const hasEarly = hasEarlyFixtureGap(entry.all)

        result.push({ matchday, firstKickoff, isLocked, isCompleted, hasEarlyFixture: hasEarly })
      }

      // Sort: completed first, then locked, then upcoming.
      // Within each group, sort by kickoff time.
      result.sort((a, b) => {
        const group = (md: PickableMatchday) =>
          md.isCompleted ? 0 : md.isLocked ? 1 : 2
        const ga = group(a), gb = group(b)
        if (ga !== gb) return ga - gb
        return a.firstKickoff.getTime() - b.firstKickoff.getTime()
      })

      return result
    },
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  })
}
